/**
 * Leviathan Web - Sidebar Component
 * Quasar Prime LLC
 * Tactical Industrial Design
 *
 * Collapsible navigation with section groups and alert badges
 */

import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import {
  LayoutDashboard,
  Truck,
  ClipboardList,
  Package,
  Users,
  FileText,
  Fuel,
  Container,
  Receipt,
  Building2,
  CreditCard,
  UserCircle,
  Settings,
  ChevronDown,
  ChevronRight,
  Menu,
  DollarSign,
  BookOpen,
  TrendingUp,
  FileSignature,
  ShieldAlert,
  Calculator,
  ScrollText,
  BarChart3,
  Mail,
  History,
  ClipboardCheck,
  Cog,
  Target,
  Phone,
  FileSpreadsheet,
  Warehouse,
  Gauge,
  Wrench,
  Shield,
  Upload,
  Palette,
} from 'lucide-react';
import { API_URL } from '@/config/api';
import { isAdminRole } from '@/constants/userRoles';
import { usePermissions } from '@/contexts/PermissionContext';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
  /**
   * The permission module this screen belongs to. Hidden unless the user holds
   * `<module>:view`.
   *
   * This is a CONVENIENCE. The API refuses independently, and is what actually
   * stops someone who types the URL. Hiding the item spares a user a 403 they
   * cannot act on; it is not the reason they cannot get in.
   */
  module?: string;
  /** Hidden from non-administrators. The server enforces this independently. */
  adminOnly?: boolean;
}

interface NavSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  items: NavItem[];
}

interface AlertCounts {
  pendingOrders: number;
  activeDispatches: number;
  lowStockItems: number;
  overdueInvoices: number;
  overdueBills: number;
}

/**
 * Sidebar navigation component with collapsible sections and alert badges
 */
const Sidebar = () => {
  const location = useLocation();
  const { accessToken, user } = useAuth();
  const { canAccessModule } = usePermissions();
  const isAdmin = isAdminRole(user?.role);
  const { settings } = useTheme();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Honor the user's "Sidebar Collapsed" preference. Re-syncs when the
  // preference changes (e.g. toggled from Appearance settings); manual
  // collapses via the chevron persist until the preference next changes.
  useEffect(() => {
    setIsCollapsed(settings.sidebarCollapsed);
  }, [settings.sidebarCollapsed]);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['operations', 'sales'])
  );
  const [alerts, setAlerts] = useState<AlertCounts>({
    pendingOrders: 0,
    activeDispatches: 0,
    lowStockItems: 0,
    overdueInvoices: 0,
    overdueBills: 0,
  });

  // Fetch alert counts on mount and periodically
  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000); // Every 5 minutes
    return () => clearInterval(interval);
  }, [accessToken]);

  const fetchAlerts = async () => {
    if (!accessToken) return;

    try {
      // Fetch stats from multiple endpoints in parallel
      const responses = await Promise.allSettled([
        fetch(`${API_URL}/api/orders/stats`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${API_URL}/api/dispatch/stats`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${API_URL}/api/inventory/stats`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${API_URL}/api/invoices/stats`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${API_URL}/api/bills/stats`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);

      const newAlerts: AlertCounts = {
        pendingOrders: 0,
        activeDispatches: 0,
        lowStockItems: 0,
        overdueInvoices: 0,
        overdueBills: 0,
      };

      // Orders stats
      if (responses[0].status === 'fulfilled' && responses[0].value.ok) {
        const data = await responses[0].value.json();
        newAlerts.pendingOrders = (data.stats?.draft || 0) + (data.stats?.confirmed || 0);
      }

      // Dispatch stats
      if (responses[1].status === 'fulfilled' && responses[1].value.ok) {
        const data = await responses[1].value.json();
        newAlerts.activeDispatches = data.stats?.enRoute || 0;
      }

      // Inventory stats
      if (responses[2].status === 'fulfilled' && responses[2].value.ok) {
        const data = await responses[2].value.json();
        newAlerts.lowStockItems = data.stats?.lowStock || 0;
      }

      // Invoice stats
      if (responses[3].status === 'fulfilled' && responses[3].value.ok) {
        const data = await responses[3].value.json();
        newAlerts.overdueInvoices = data.stats?.overdue || 0;
      }

      // Bills stats
      if (responses[4].status === 'fulfilled' && responses[4].value.ok) {
        const data = await responses[4].value.json();
        newAlerts.overdueBills = data.stats?.overdue || 0;
      }

      setAlerts(newAlerts);
    } catch (error) {
      // Silently fail - badges will just show 0
    }
  };

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  /**
   * Whether a navigation item should be rendered for this user.
   *
   * Presentation only. The API refuses independently — see
   * apps/api/src/middleware/requirePermission.ts.
   */
  const isItemVisible = (item: NavItem): boolean => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.module && !canAccessModule(item.module)) return false;
    return true;
  };

  const isPathActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Navigation sections with all modules
  const navSections: NavSection[] = [
    {
      id: 'operations',
      title: 'Operations',
      icon: <Truck className="w-4 h-4" />,
      items: [
        {
          path: '/dashboard',
          module: 'dashboard',
          label: 'Dashboard',
          icon: <LayoutDashboard className="w-5 h-5" />,
        },
        {
          path: '/dispatch',
          module: 'dispatch',
          label: 'Dispatch',
          icon: <Truck className="w-5 h-5" />,
          badge: alerts.activeDispatches,
        },
        {
          path: '/dispatch/routes',
          module: 'dispatch',
          label: 'Driver Routes',
          icon: <ClipboardList className="w-5 h-5" />,
        },
        {
          path: '/orders',
          module: 'orders',
          label: 'Orders',
          icon: <ClipboardList className="w-5 h-5" />,
          badge: alerts.pendingOrders,
        },
        {
          path: '/shipments',
          module: 'shipments',
          label: 'Shipments',
          icon: <Package className="w-5 h-5" />,
        },
        {
          path: '/bol',
          module: 'bol',
          label: 'Bill of Lading',
          icon: <ScrollText className="w-5 h-5" />,
        },
        {
          path: '/operations/delivery-programs',
          module: 'delivery_programs',
          label: 'Delivery Programs',
          icon: <Gauge className="w-5 h-5" />,
        },
      ],
    },
    {
      id: 'crm',
      title: 'CRM',
      icon: <Target className="w-4 h-4" />,
      items: [
        {
          path: '/sales',
          module: 'crm',
          label: 'Sales Dashboard',
          icon: <Target className="w-5 h-5" />,
        },
        {
          path: '/sales/leads',
          module: 'crm',
          label: 'Leads',
          icon: <Users className="w-5 h-5" />,
        },
        {
          path: '/sales/activities',
          module: 'crm',
          label: 'Activities',
          icon: <Phone className="w-5 h-5" />,
        },
        {
          path: '/sales/quotes',
          module: 'crm',
          label: 'Quotes',
          icon: <FileSpreadsheet className="w-5 h-5" />,
        },
      ],
    },
    {
      id: 'sales',
      title: 'Customers',
      icon: <Users className="w-4 h-4" />,
      items: [
        {
          path: '/customers',
          module: 'customers',
          label: 'Customers',
          icon: <Users className="w-5 h-5" />,
        },
        {
          path: '/invoices',
          module: 'invoices',
          label: 'Invoices',
          icon: <FileText className="w-5 h-5" />,
          badge: alerts.overdueInvoices,
        },
        {
          path: '/credit',
          module: 'credit',
          label: 'Credit Mgmt',
          icon: <ShieldAlert className="w-5 h-5" />,
        },
        {
          path: '/accounting/accounts-receivable',
          module: 'accounting',
          label: 'Open AR',
          icon: <DollarSign className="w-5 h-5" />,
        },
      ],
    },
    {
      id: 'pricing',
      title: 'Pricing',
      icon: <DollarSign className="w-4 h-4" />,
      items: [
        {
          path: '/pricing/pricebooks',
          module: 'pricing',
          label: 'Price Books',
          icon: <BookOpen className="w-5 h-5" />,
        },
        {
          path: '/pricing/rackprices',
          module: 'pricing',
          label: 'Rack Prices',
          icon: <TrendingUp className="w-5 h-5" />,
        },
        {
          path: '/pricing/terminal-rack-prices',
          module: 'pricing',
          label: 'Terminal Prices',
          icon: <Warehouse className="w-5 h-5" />,
        },
        {
          path: '/pricing/contracts',
          module: 'pricing',
          label: 'Contracts',
          icon: <FileSignature className="w-5 h-5" />,
        },
      ],
    },
    {
      id: 'inventory',
      title: 'Inventory',
      icon: <Container className="w-4 h-4" />,
      items: [
        {
          path: '/inventory',
          module: 'inventory',
          label: 'Products',
          icon: <Fuel className="w-5 h-5" />,
          badge: alerts.lowStockItems,
        },
        {
          path: '/tanks',
          module: 'inventory',
          label: 'Tanks',
          icon: <Container className="w-5 h-5" />,
        },
        {
          path: '/receipts',
          module: 'inventory',
          label: 'Receipts',
          icon: <Receipt className="w-5 h-5" />,
        },
        {
          path: '/liftings',
          module: 'inventory',
          label: 'Liftings',
          icon: <Truck className="w-5 h-5" />,
        },
      ],
    },
    {
      id: 'fleet',
      title: 'Fleet',
      icon: <Truck className="w-4 h-4" />,
      items: [
        {
          path: '/trucks',
          module: 'fleet',
          label: 'Trucks',
          icon: <Truck className="w-5 h-5" />,
        },
        {
          path: '/drivers',
          module: 'fleet',
          label: 'Drivers',
          icon: <UserCircle className="w-5 h-5" />,
        },
        {
          // Third-party haulers — fleet capacity the distributor does not own.
          path: '/carriers',
          module: 'fleet',
          label: 'Carriers',
          icon: <Truck className="w-5 h-5" />,
        },
        {
          path: '/fleet/maintenance',
          module: 'fleet',
          label: 'Maintenance',
          icon: <Wrench className="w-5 h-5" />,
        },
      ],
    },
    {
      id: 'purchasing',
      title: 'Purchasing',
      icon: <CreditCard className="w-4 h-4" />,
      items: [
        {
          path: '/suppliers',
          module: 'purchasing',
          label: 'Suppliers',
          icon: <Building2 className="w-5 h-5" />,
        },
        {
          path: '/terminals',
          module: 'purchasing',
          label: 'Terminals',
          icon: <Warehouse className="w-5 h-5" />,
        },
        {
          path: '/ap',
          module: 'purchasing',
          label: 'Bills (AP)',
          icon: <CreditCard className="w-5 h-5" />,
          badge: alerts.overdueBills,
        },
      ],
    },
    {
      id: 'reports',
      title: 'Reports',
      icon: <BarChart3 className="w-4 h-4" />,
      items: [
        {
          path: '/reports',
          module: 'reports',
          label: 'All Reports',
          icon: <BarChart3 className="w-5 h-5" />,
        },
        {
          path: '/reports/statements',
          module: 'accounting',
          label: 'Statements',
          icon: <FileText className="w-5 h-5" />,
        },
        {
          path: '/reports/reconciliation',
          module: 'accounting',
          label: 'Net/Gross Recon',
          icon: <Calculator className="w-5 h-5" />,
        },
        {
          path: '/reports/tax-exports',
          module: 'tax',
          label: 'Tax Exports',
          icon: <FileText className="w-5 h-5" />,
        },
      ],
    },
    {
      id: 'admin',
      title: 'Admin',
      icon: <Settings className="w-4 h-4" />,
      items: [
        {
          path: '/admin/users',
          module: 'admin',
          label: 'Users',
          icon: <Users className="w-5 h-5" />,
        },
        {
          path: '/admin/roles',
          module: 'admin',
          label: 'Permission Groups',
          icon: <Shield className="w-5 h-5" />,
          adminOnly: true,
        },
        {
          path: '/admin/customer-type-templates',
          module: 'admin',
          label: 'Customer Types',
          icon: <ClipboardList className="w-5 h-5" />,
        },
        {
          path: '/admin/import-export',
          module: 'admin',
          label: 'Import/Export',
          icon: <Upload className="w-5 h-5" />,
          adminOnly: true,
        },
        {
          path: '/settings/appearance',
          label: 'Appearance',
          icon: <Palette className="w-5 h-5" />,
        },
        {
          path: '/settings',
          module: 'admin',
          label: 'Settings',
          icon: <Settings className="w-5 h-5" />,
        },
        {
          path: '/settings/tax',
          module: 'tax',
          label: 'Tax Rates',
          icon: <Calculator className="w-5 h-5" />,
        },
        {
          path: '/settings/tax/configuration',
          module: 'tax',
          adminOnly: true,
          label: 'Tax Configuration',
          icon: <Calculator className="w-5 h-5" />,
        },
        {
          // Not adminOnly: anyone who can see tax benefits from knowing whether
          // it is configured, and every link it offers enforces its own
          // permissions at the far end.
          path: '/settings/tax/readiness',
          module: 'tax',
          label: 'Tax Readiness',
          icon: <ClipboardCheck className="w-5 h-5" />,
        },
        {
          path: '/settings/email',
          module: 'admin',
          label: 'Email Settings',
          icon: <Mail className="w-5 h-5" />,
        },
        {
          path: '/settings/email/templates',
          module: 'admin',
          label: 'Email Templates',
          icon: <FileText className="w-5 h-5" />,
        },
        {
          path: '/settings/email/logs',
          module: 'admin',
          label: 'Email Logs',
          icon: <History className="w-5 h-5" />,
        },
        {
          path: '/settings/audit',
          module: 'admin',
          label: 'Audit Logs',
          icon: <ClipboardCheck className="w-5 h-5" />,
        },
        {
          path: '/settings/system',
          module: 'admin',
          label: 'System Settings',
          icon: <Cog className="w-5 h-5" />,
        },
      ],
    },
  ];

  return (
    <aside
      className={`${isCollapsed ? 'w-16' : 'w-64'} min-h-[calc(100vh-4rem)] bg-panel-dark border-r border-panel-border flex flex-col transition-all duration-300 relative`}
    >
      {/* Collapse toggle */}
      <div className="absolute -right-3 top-4 z-10">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="w-6 h-6 bg-panel-dark border border-panel-border hover:border-amber text-text-tertiary hover:text-amber flex items-center justify-center transition-colors"
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <Menu className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {/* Sections whose every item is hidden are dropped entirely — an empty
            "Accounting" heading tells a salesperson exactly what they cannot
            reach, which is neither useful to them nor to the operator. */}
        {navSections
          .filter((section) => section.items.some(isItemVisible))
          .map((section) => (
          <div key={section.id} className="mb-2">
            {/* Section Header */}
            {!isCollapsed && (
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between px-4 py-2 text-text-tertiary hover:text-text-secondary transition-colors"
              >
                <span className="flex items-center space-x-2">
                  {section.icon}
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em]">
                    {section.title}
                  </span>
                </span>
                {expandedSections.has(section.id) ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
              </button>
            )}

            {/* Section Items */}
            {(isCollapsed || expandedSections.has(section.id)) && (
              <div className={`${isCollapsed ? '' : 'ml-2'} space-y-0.5`}>
                {section.items
                  .filter(isItemVisible)
                  .map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={`group flex items-center ${isCollapsed ? 'justify-center px-2' : 'px-4'} py-2.5 mx-2 transition-all relative ${
                      isPathActive(item.path)
                        ? 'bg-amber/10 text-amber border-l-2 border-amber'
                        : 'text-text-secondary hover:text-text-primary hover:bg-panel-border/30 border-l-2 border-transparent'
                    }`}
                    title={isCollapsed ? item.label : undefined}
                  >
                    {/* Active glow effect */}
                    {isPathActive(item.path) && (
                      <div
                        className="absolute inset-0 bg-amber/5"
                        style={{ filter: 'blur(4px)' }}
                      />
                    )}

                    {/* Icon */}
                    <span
                      className={`relative z-10 ${
                        isPathActive(item.path)
                          ? 'text-amber drop-shadow-[0_0_6px_rgba(255,149,0,0.8)]'
                          : ''
                      }`}
                    >
                      {item.icon}
                    </span>

                    {/* Label */}
                    {!isCollapsed && (
                      <span className="relative z-10 ml-3 font-mono text-xs uppercase tracking-wider">
                        {item.label}
                      </span>
                    )}

                    {/* Badge */}
                    {item.badge !== undefined && item.badge > 0 && (
                      <span
                        className={`${isCollapsed ? 'absolute -top-1 -right-1' : 'ml-auto'} bg-error-red text-white text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center relative z-10`}
                        style={{ boxShadow: '0 0 6px rgba(239,68,68,0.6)' }}
                      >
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}

                    {/* Collapsed tooltip */}
                    {isCollapsed && (
                      <div className="absolute left-full ml-2 px-3 py-1.5 bg-panel-dark border border-amber text-amber font-mono text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg">
                        {item.label}
                        {item.badge !== undefined && item.badge > 0 && (
                          <span className="ml-2 text-error-red">({item.badge})</span>
                        )}
                      </div>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-panel-border p-3 bg-background/50">
        {!isCollapsed ? (
          <div className="text-center">
            <div className="text-[10px] text-text-tertiary font-mono uppercase tracking-wider">
              Leviathan v0.1.0
            </div>
            <div className="text-[9px] text-text-tertiary font-mono mt-0.5">
              Quasar Prime LLC
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <div
              className="w-6 h-6 bg-amber/20 flex items-center justify-center"
              style={{
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
              }}
            >
              <span className="text-amber font-bold text-[10px]">L</span>
            </div>
          </div>
        )}
      </div>

      {/* Decorative corners */}
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber/30" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber/30" />
    </aside>
  );
};

export default Sidebar;
